import { CaretDown, Plus } from '@phosphor-icons/react';
import { lowerFirst } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IAuthUser, ICompanySettings } from 'auth/types';
import { ExportType } from 'cli/enums';
import {
  ActionButtonType,
  ApiStatus,
  ExportEntity,
  FileType,
  NotificationPosition,
} from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { IConfirmation, ITableAction } from 'core/types';
import { isEmptyArray } from 'core/utils';
import { usePartActions } from 'parts/hooks';
import {
  useExpiredCostingContext,
  useQuoteVersioningContext,
} from 'quotes/components/CustomerQuotePartList';
import { QuotePartListAction } from 'quotes/components/QuotePartList';
import {
  QuoteEntityType,
  QuotePartListTriggerAction,
  QuoteStatus,
  QuoteType,
  QuoteVersionType,
} from 'quotes/enums';
import { useQuoteActions, useQuoteVersionActions } from 'quotes/hooks';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart } from 'quotes/types';
import { getActiveQuoteParts, isOrderEditLocked } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { getOrderActionItem } from './utils';

/**
 * Actions for Customer Quote/Order Part List
 *
 * When current quote version is 'Draft':
 *  - Add (Add Line, Add Pricing Group Discount)
 *  - Import
 *  - Release (Release, View release, Reset version) - hidden if entity type is 'Order'
 *  - Download (XLS, PDF, Data Sheets)
 *  - Reset Expired Costs (count) (if expiredCostingLinesCount > 0)
 *
 * When current quote version is 'Release':
 *  - Download (XLS, PDF, Data Sheets)
 *  - Edit Quote (entity type 'Quote') / Edit Order (entity type 'Order')
 *  - Order All (only for Quote entity type)
 *      (not available if quote does not contain at least one 'Part' record type part)
 *  - Submit Order (only for entity type 'Order')
 *      (not available if quote status is DRAFT_VENDOR or REQUESTED_BY_CUSTOMER or if quote does not
 *      contain at least one 'Part' record type part)
 *  - Reset Expired Costs (count) (if expiredCostingLinesCount > 0)
 */
export const useActions = (
  quote: ICustomerQuote,
  user: IAuthUser,
  setConfirmationOption: Dispatch<SetStateAction<IConfirmation>>,
  quoteEntityType = QuoteEntityType.QUOTE,
  trigger: QuotePartListTriggerAction = null
) => {
  const [activeActionDrawer, setActiveActionDrawer] = useState<QuotePartListAction>(null);

  const { t } = useTranslation();

  const { showNotification, isLoadingTopNotificationDisplayed } = useNotificationContext();
  const {
    onInitDraftSuccess,
    onInitDraftError,
    onInitDraftPollFailed,
    onResetDraftSuccess,
    onResetDraftError,
    onResetDraftPollFailed,
    onChangeVersionView,
    initDraftButtonDisabled,
    setInitDraftButtonDisabled,
    releaseButtonDisabled,
    setReleaseButtonDisabled,
    resetButtonDisabled,
    setResetButtonDisabled,
  } = useQuoteVersioningContext();

  const { isDraftVersionChanged, currentQuoteVersion } = useCustomerQuotePartsContext();
  const { rows, isDataLoadCompleted } = useAgGridClientTableContext();
  const { displayResetExpiredCostsActionItem, expiredCostingLinesCount, onResetExpiredCosts } =
    useExpiredCostingContext();

  const { handleCliJob, handleCliJobExport } = useCliJobHandler();
  const { exportQuote } = useQuoteActions();
  const { initializeDraftVersion, resetDraftVersion } = useQuoteVersionActions();
  const { exportPartDatasheets } = usePartActions();

  const activeRowData = getActiveQuoteParts(rows as IQuotePart[]);

  const companySettings: ICompanySettings = user?.companySettings;

  const downloadQuote = useCallback(
    (fileType: FileType) => {
      const exportData = {
        quoteId: quote?.id,
        exportType: ExportEntity.QUOTE,
        options: {
          fileType,
          quoteType: QuoteType.CUSTOMER,
          quoteVersionType: currentQuoteVersion,
        },
      };

      const exportOptions = {
        exportInProgressMessage: t('messageGenerateExportFileTypeInProgress', {
          fileType: fileType.toUpperCase(),
        }),
        exportSuccessMessage: t('messageGenerateExportFileTypeSuccess', {
          fileType: fileType.toUpperCase(),
        }),
      };

      handleCliJobExport(exportQuote, exportData, exportOptions);
    },
    [quote?.id, currentQuoteVersion, t, handleCliJobExport, exportQuote]
  );

  useEffect(() => {
    if (trigger === QuotePartListTriggerAction.DOWNLOAD_XLS) {
      downloadQuote(FileType.XLS);
    }

    if (trigger === QuotePartListTriggerAction.DOWNLOAD_PDF) {
      downloadQuote(FileType.PDF);
    }
  }, [downloadQuote, trigger]);

  const actions = (): ITableAction[] => {
    const downloadActionItem = {
      type: ActionButtonType.MENU,
      label: t('buttonLabelDownload'),
      disabled: activeRowData?.length === 0,
      menuItems: [
        {
          label: t('buttonLabelDownloadXls'),
          onClick: () => downloadQuote(FileType.XLS),
        },
        {
          label: t('buttonLabelDownloadPdf'),
          onClick: () => downloadQuote(FileType.PDF),
        },
        {
          label: t('buttonLabelDownloadDataSheets'),
          onClick: () => downloadDataSheetArchive(activeRowData),
          disabled: !isDataLoadCompleted,
        },
      ],
      hide: QuoteEntityType.ORDER === quoteEntityType,
    };

    // Disable release button if the draft is not changed (compared to the release version) or
    // for a newly created quote that is still in its first draft state and have no parts are added
    const releaseResetButtonDisabled =
      releaseButtonDisabled ||
      resetButtonDisabled ||
      !isDraftVersionChanged ||
      (!quote.draftVersionNumber && rows?.length === 0);

    const releaseActionsItem = {
      type: ActionButtonType.GROUP,
      buttons: [
        {
          type: ActionButtonType.BUTTON,
          label: t('buttonLabelRelease'),
          disabled: releaseResetButtonDisabled,
          variant: !releaseResetButtonDisabled ? Variant.SOLID : Variant.OUTLINED,
          onClick: () => {
            setReleaseButtonDisabled(true);
            setActiveActionDrawer(QuotePartListAction.RELEASE_VERSION);
          },
        },
        {
          type: ActionButtonType.MENU,
          icon: <CaretDown weight="fill" />,
          endDecorator: null,
          variant: Variant.SOLID,
          menuItems: [
            {
              label: t('buttonLabelViewReleaseVersion'),
              onClick: () => onChangeVersionView(QuoteVersionType.RELEASE),
            },
            {
              label: t('buttonLabelResetVersion'),
              disabled: releaseResetButtonDisabled,
              onClick: () => {
                setConfirmationOption({
                  title: t('titleResetVersion'),
                  message: t('messageConfirmResetQuoteDraftVersion'),
                  cancelBtnLabel: t('buttonLabelKeepDraftVersion'),
                  confirmBtnLabel: t('buttonLabelReset'),
                  confirmBtnColor: Color.SUCCESS,
                  onConfirm: () => {
                    setResetButtonDisabled(true);
                    setConfirmationOption(null);

                    handleCliJob(
                      resetDraftVersion,
                      { quoteId: quote?.id },
                      {
                        cliJobInProgressMessage: t('messageQuoteVersionResetInProgress', {
                          entityType: lowerFirst(quoteEntityType),
                        }),
                        onCliJobFinished: onResetDraftSuccess,
                        onCliJobStopped: onResetDraftError,
                        onCliJobPollFailed: onResetDraftPollFailed,
                      }
                    );
                  },
                  onCancel: () => setConfirmationOption(null),
                });
              },
            },
          ],
        },
      ],
    };

    const resetExpiredCostingActionItem = {
      type: ActionButtonType.BUTTON,
      label: t('buttonLabelResetExpiredCosts', { count: expiredCostingLinesCount }),
      color: Color.WARNING,
      onClick: () => onResetExpiredCosts(),
      hide: !displayResetExpiredCostsActionItem,
    };

    if (QuoteVersionType.DRAFT === currentQuoteVersion) {
      return [
        {
          type: ActionButtonType.BUTTON,
          label: t('labelAddLine'),
          color: Color.SUCCESS,
          variant: isDataLoadCompleted ? Variant.SOLID : Variant.SOFT,
          startDecorator: <Plus />,
          onClick: () => setActiveActionDrawer(QuotePartListAction.ADD_LINE),
          disabled: !isDataLoadCompleted || isLoadingTopNotificationDisplayed,
        },
        {
          type: ActionButtonType.BUTTON,
          label: t('buttonLabelImport'),
          onClick: () => setActiveActionDrawer(QuotePartListAction.IMPORT),
          disabled: !isDataLoadCompleted || isLoadingTopNotificationDisplayed,
        },
        {
          ...releaseActionsItem,
          hide: QuoteEntityType.ORDER === quoteEntityType,
        },
        downloadActionItem,
        resetExpiredCostingActionItem,
      ];
    }

    // Editing an order the external system may already hold would desync it, so the draft
    // action stays locked until the submit resolves one way or the other.
    const disabledDraftActionItem =
      initDraftButtonDisabled ||
      isLoadingTopNotificationDisplayed ||
      (quoteEntityType === QuoteEntityType.ORDER && isOrderEditLocked(quote.status));

    const draftActionItem = {
      type: ActionButtonType.BUTTON,
      label: t('buttonLabelEditQuote', { entityType: quoteEntityType }),
      variant: disabledDraftActionItem ? Variant.OUTLINED : Variant.SOLID,
      color: Color.WARNING,
      disabled: disabledDraftActionItem,
      onClick: () => {
        setInitDraftButtonDisabled(true);

        handleCliJob(
          initializeDraftVersion,
          { quoteId: quote?.id },
          {
            cliJobInProgressMessage: t('messageQuoteDraftVersionInitializeInProgress'),
            onCliJobFinished: onInitDraftSuccess,
            onCliJobStopped: onInitDraftError,
            onCliJobPollFailed: onInitDraftPollFailed,
          }
        );
      },
      hide: QuoteEntityType.ORDER === quoteEntityType && QuoteStatus.ORDER_ORDERED === quote.status,
      sx: disabledDraftActionItem
        ? {
            pointerEvents: 'auto !important',
            cursor: 'not-allowed !important',
            '&:hover': { backgroundColor: 'transparent !important' },
          }
        : {},
    };

    const orderAllActionItem = getOrderActionItem(
      activeRowData as ICustomerQuotePart[],
      currentQuoteVersion,
      Number(companySettings?.minimumOrderThreshold),
      setConfirmationOption,
      () => {
        setConfirmationOption(null);
        setActiveActionDrawer(QuotePartListAction.CREATE_ORDER);
      },
      () => setConfirmationOption(null),
      quoteEntityType
    );

    return [
      downloadActionItem,
      draftActionItem,
      {
        ...(orderAllActionItem as ITableAction),
        disabled:
          !isDataLoadCompleted || orderAllActionItem?.disabled || isLoadingTopNotificationDisplayed,
      },
    ];
  };

  const downloadDataSheetArchive = (rowData = []) => {
    const activeQuotePartIds = rowData?.map((quotePart) => {
      if (1 === quotePart.verified && 0 === quotePart.deleted) {
        return quotePart['partId'];
      }
    });

    if (isEmptyArray(activeQuotePartIds)) {
      showNotification({
        title: t('titleError'),
        type: ApiStatus.ERROR,
        message: t('errorNoPartsProvidedForDatasheetExport'),
        position: NotificationPosition.TOP,
      });

      return;
    }
    const exportData = {
      partIds: activeQuotePartIds,
    };

    const exportOptions = {
      exportInProgressMessage: t('messageGeneratePartsDatasheetArchiveInProgress'),
      exportSuccessMessage: t('messageGeneratePartsDatasheetArchiveSuccess'),
    };

    handleCliJobExport(exportPartDatasheets, exportData, exportOptions, ExportType.ARCHIVE);
  };

  return {
    activeActionDrawer,
    setActiveActionDrawer,
    onCloseActionDrawer: () => setActiveActionDrawer(null),
    actions,
  };
};
